/**
 * Shared Auth Hook — Cognito / Amplify v6
 *
 * Provides a unified useAuth() hook that:
 * - Loads the current Cognito session on mount
 * - Extracts roles from the ID token's cognito:groups claim
 * - Exposes isAuthenticated, user, logout
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
  type AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import type { User, UserRole } from '../../../shared/types';
import { trpcClient } from '@/lib/trpc';
import { markExplicitLogout } from '@/lib/return-to';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const ALL_VALID_ROLES: UserRole[] = [
  'admin', 'tech', 'finance', 'people', 'clapper', 'growth', 'user',
  'branding', 'business', 'creative', 'data', 'executive',
  'ownedmedia', 'paidmedia', 'partnerships',
];

function parseRolesFromGroups(groups: string[]): UserRole[] {
  const roles = groups.filter((g) => ALL_VALID_ROLES.includes(g as UserRole)) as UserRole[];
  return roles.length > 0 ? roles : ['user'];
}

async function loadUser(): Promise<User | null> {
  try {
    const cognitoUser: AuthUser = await getCurrentUser();
    const session = await fetchAuthSession();

    // Extract groups from the ID token's cognito:groups claim
    const idTokenPayload = session.tokens?.idToken?.payload;
    const groups = (idTokenPayload?.['cognito:groups'] as string[]) || [];
    const roles = parseRolesFromGroups(groups);
    const primaryRole: UserRole = roles.includes('admin')
      ? 'admin'
      : roles.find((r) => r !== 'user') || roles[0] || 'user';

    const email =
      (idTokenPayload?.['email'] as string) ||
      (idTokenPayload?.['cognito:username'] as string) ||
      cognitoUser.username;

    const name =
      (idTokenPayload?.['name'] as string) ||
      email.split('@')[0];

    const extraData = await trpcClient.extraPermissions.getMine.query()
      .catch(() => ({ tabs: [] as string[], deniedTabs: [] as string[] }));

    return {
      userId: cognitoUser.userId,
      email,
      name,
      role: primaryRole,
      roles,
      extraPermissions: extraData.tabs,
      deniedPermissions: extraData.deniedTabs,
      isActive: true,
      createdAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const refresh = useCallback(async () => {
    const user = await loadUser();
    setState({
      user,
      isLoading: false,
      isAuthenticated: !!user,
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for Amplify auth events (e.g., Google OAuth callback completing sign-in)
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        refresh();
      }
      if (payload.event === 'signedOut') {
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    });
    return unsubscribe;
  }, [refresh]);

  const logout = useCallback(async () => {
    markExplicitLogout();
    await amplifySignOut();
    setState({ user: null, isLoading: false, isAuthenticated: false });
  }, []);

  return {
    user: state.user,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    roles: state.user?.roles ?? [],
    isAdmin: state.user?.roles.includes('admin') ?? false,
    logout,
    refreshUser: refresh,
  };
}
